<template>
  <div>
    <router-view id="content" />
    <el-dialog
      v-model="showMapboxCookieDialog"
      custom-class="mapbox-cookie-dialog"
      :append-to-body="true"
      :close-on-click-modal="false"
      :top="'0'"
      :show-close="false"
    >
      <MapboxCookieDialog />
    </el-dialog>
  </div>
</template>

<script>
import MapboxCookieDialog from '@/components/MapboxCookieDialog.vue'
import { convertFromSlug } from '@/utils/format.js'
import { redirectToLogin } from '@/utils/misc.js'
import { mapGetters } from 'vuex'

export default {
  components: {
    MapboxCookieDialog,
  },
  data() {
    return {
      htmlNavigation: '',
      htmlFooter: '',
      cssLoaded: false,
    }
  },
  // beforeCreate() {
  // 	this.$store.commit('initialiseStore')
  // },
  created() {
    // Restore lockedTimeslot from sessionStorage if available
    this.restoreLockedTimeslot()

    this.verifyLogin().then((isLoggedIn) => {
      if (isLoggedIn) {
        this.userLoggedInHandler()
      } else {
        this.userLoggedOutHandler()
      }
    })

    this.$store.commit('setIsMobile', window.innerWidth < 480)
    window.addEventListener('resize', () => {
      this.$store.commit('setIsMobile', window.innerWidth < 480)
    })
  },
  computed: {
    ...mapGetters([
      'sessionData',
      'user',
      'activeStep',
      'federalStates',
      'selectedState',
      'selectedServicesId',
      'selectedServicesLoaded',
      'userVehicles',
      'selectedVehicle',
      'kfzartId',
      'showMapboxCookieDialog',
      'activeStationId',
      'selectedTimeslot',
    ]),
    routeHasVehicleId() {
      return this.$route.query.kfzid || this.$route.query.kfzartid
    },
    routeHasDateTime() {
      return this.$route.query.tag && this.$route.query.von
    },
    isServiceRoute() {
      return (
        this.$route.query.bundesland &&
        !this.$route.params.services &&
        !this.routeHasVehicleId &&
        !this.$route.params.station &&
        !this.routeHasDateTime
      )
    },
    isVehicleRoute() {
      return (
        this.$route.query.bundesland &&
        this.$route.params.services &&
        !this.routeHasVehicleId &&
        !this.$route.params.station &&
        !this.routeHasDateTime
      )
    },
    isStationRoute() {
      return (
        this.$route.query.bundesland &&
        this.$route.params.services &&
        this.routeHasVehicleId &&
        !this.$route.params.station &&
        !this.routeHasDateTime
      )
    },
    isAppointmentRoute() {
      return (
        this.$route.query.bundesland &&
        this.$route.params.services &&
        this.routeHasVehicleId &&
        this.$route.params.station &&
        !this.routeHasDateTime
      )
    },
    isOverviewRoute() {
      return (
        this.$route.query.bundesland &&
        this.$route.params.services &&
        this.routeHasVehicleId &&
        this.$route.params.station &&
        this.routeHasDateTime
      )
    },
    hasAllServices() {
      return this.selectedServicesId?.length === this.$route.params.services?.split('+').length
    },
    loadedEitherVehicle() {
      return this.selectedVehicle === false || this.kfzartId
    },
    loadedServicePage() {
      return this.isServiceRoute && this.selectedState !== null
    },
    loadedVehiclePage() {
      return this.isVehicleRoute && this.selectedState !== null && this.selectedServicesLoaded
    },
    loadedStationPage() {
      return (
        this.isStationRoute &&
        this.selectedState !== null &&
        this.selectedServicesLoaded &&
        this.loadedEitherVehicle
      )
    },
    loadedAppointmentPage() {
      return (
        this.isAppointmentRoute &&
        this.selectedState !== null &&
        this.selectedServicesLoaded &&
        this.loadedEitherVehicle &&
        this.activeStationId !== null
      )
    },
    loadedOverviewPage() {
      return (
        this.isOverviewRoute &&
        this.selectedState !== null &&
        this.selectedServicesLoaded &&
        this.loadedEitherVehicle &&
        this.activeStationId !== null &&
        this.selectedTimeslot !== null
      )
    },
  },
  watch: {
    sessionData() {
      if (this.sessionData) {
        // called only when entering via url and sessionData is loaded
        if (this.isServiceRoute) {
          this.loadServicePage()
        } else if (this.isVehicleRoute) {
          this.loadVehiclePage()
        } else if (this.isStationRoute) {
          this.loadStationPage()
        } else if (this.isAppointmentRoute) {
          this.loadAppointmentPage()
        } else if (this.isOverviewRoute) {
          this.loadOverviewPage()
        } else if (!this.$route.params.appointmentId) {
          this.$router.replace({ name: 'Home' })
        }
      }
    },
    loadedServicePage() {
      if (this.loadedServicePage && this.activeStep !== 1) {
        this.selectedState ? this.goToStep(1) : this.goToStep(0)
      }
    },
    loadedVehiclePage() {
      if (this.loadedVehiclePage && this.activeStep !== 2) this.calculateStep()
    },
    loadedStationPage() {
      if (this.loadedStationPage && this.activeStep !== 3) this.calculateStep()
    },
    loadedAppointmentPage() {
      if (this.loadedAppointmentPage && this.activeStep !== 4) this.calculateStep()
    },
    loadedOverviewPage() {
      if (this.loadedOverviewPage && this.activeStep !== 5) this.calculateStep()
    },
    kfzartId() {
      if (this.kfzartId && this.$route.query.tag && this.$route.query.von) {
        this.populateAppointment()
      }
    },
  },
  methods: {
    redirectToLogin,
    /**
     * Restores locked timeslot from sessionStorage after page reload.
     * This preserves vehicle data (terminFahrzeugModel) for manually entered vehicles.
     * The timeslot itself may no longer be available, but vehicle data can be recovered.
     */
    restoreLockedTimeslot() {
      try {
        const saved = sessionStorage.getItem('tvg_lockedTimeslot')
        if (saved) {
          const lockedTimeslot = JSON.parse(saved)
          this.$store.commit('setLockedTimeslot', lockedTimeslot)
        }
      } catch (error) {
        console.error('Failed to restore lockedTimeslot from sessionStorage:', error)
        sessionStorage.removeItem('tvg_lockedTimeslot')
      }
    },
    async verifyLogin() {
      try {
        await this.$store.dispatch('getUserSessionData')
        return !!this.sessionData
      } catch (error) {
        console.error(error)
      }
    },
    parseHTML(html) {
      let t = document.createElement('template')
      t.innerHTML = html
      return t.content.cloneNode(true)
    },
    async userLoggedInHandler() {
      // await this.$store.dispatch('getUserSessionData')
      await this.$store.dispatch('getUserAccountInfo')
      this.$store.dispatch('getContractId')
    },
    async userLoggedOutHandler() {
      // Check if the current route is one of our whitelisted routes
      const currentRouteName = this.$router.currentRoute.value.name
      const whitelistedRoutes = ['Home', 'AppointmentId']

      // Only redirect if not on a whitelisted route
      if (!whitelistedRoutes.includes(currentRouteName)) {
        await this.$router.push({ name: 'Home' })
      }

      this.$store.dispatch('clearAllState')
    },
    /**
     * Loads all required data for the overview page (Step 5) in the correct order.
     * Handles dependencies between data (e.g. services need state, vehicle needs user).
     * Validates all data before attempting to load/lock appointment.
     * 
     * Dependency chain:
     * 1. User data (required for vehicle loading)
     * 2. State (required for services loading)
     * 3. Services (requires state.landesclubId)
     * 4. Vehicle & Station (parallel, independent)
     * 5. Validation (ensures all data present)
     * 6. Appointment (requires all above data)
     */
    async loadOverviewPage() {
      // Phase 1: Ensure user data is loaded first (required for vehicle loading)
      if (!this.user?.pnr) {
        await this.userLoggedInHandler()
      }

      // Phase 2: Load state first (services depends on selectedState.landesclubId)
      await this.populateState()

      // Phase 3: Load services (needs selectedState.landesclubId from phase 2)
      await this.populateServices()

      // Phase 4: Load vehicle and station in parallel (independent of each other)
      await Promise.all([
        this.populateVehicle(),
        this.populateStation(),
      ])

      // Phase 5: Validate all required data is present before proceeding
      if (!this.validateRequiredData()) {
        console.error('Required data not loaded for appointment booking', {
          user: !!this.user?.pnr,
          station: !!this.$store.state.station.activeStationId,
          services: !!this.$store.state.service.selectedServicesId?.length,
          vehicle: !!(this.$store.state.vehicle.selectedVehicle || this.$store.state.vehicle.newVehicle)
        })
        return
      }

      // Phase 6: Load and lock appointment (requires all data from phases 1-4)
      await this.populateAppointment()
    },
    async loadAppointmentPage() {
      await this.populateState()
      await this.populateServices()
      await this.populateVehicle()
      await this.populateStation()
    },
    async loadStationPage() {
      await this.populateState()
      await this.populateServices()
      await this.populateVehicle()
    },
    async loadVehiclePage() {
      await this.populateState()
      await this.populateServices()
    },
    async loadServicePage() {
      await this.populateState()
    },
    async populateState() {
      await this.$store.dispatch('getFederalStates')
      await this.$store.dispatch('getLandesclubsTelephone')

      const bezeichnung = convertFromSlug(this.$route.query.bundesland)
      const state = this.federalStates.find((state) => state.bezeichnung === bezeichnung)

      if (state) {
        this.$store.commit('setSelectedState', state)
      } else {
        this.$store.commit('setSelectedState', false)
      }
    },
    async populateServices() {
      await this.$store.dispatch('getServices')
      this.$store.commit('setSelectedServicesId', [])
      this.$store.commit('setSelectedServicesName', [])
      this.$route.params.services.split('+').forEach((serviceSlug) => {
        this.$store.dispatch('getSnrMappings', serviceSlug)
      })
    },
    /**
     * Loads vehicle data from user's saved vehicles or restores manually entered vehicle data.
     * For manually entered vehicles, attempts to restore full data from lockedTimeslot (if available from sessionStorage).
     */
    async populateVehicle() {
      await this.$store.dispatch('getUserVehicles', this.user.pnr)
      
      // Handle registered vehicles from user account
      if (this.$route.query.kfzid) {
        const vehicle = this.userVehicles.find(
          (vehicle) => vehicle.vehicle.id === +this.$route.query.kfzid
        )?.vehicle

        if (vehicle) {
          this.$store.commit('setSelectedVehicle', vehicle)
        } else {
          this.$store.commit('setSelectedVehicle', false)
        }
      }
      
      // Handle manually entered vehicles or services without specific vehicle (e.g. Fahrradservice)
      if (this.$route.query.kfzartid) {
        // Try to restore full vehicle data from lockedTimeslot (survives page reload via sessionStorage)
        const lockedTimeslot = this.$store.state.appointment.lockedTimeslot
        if (lockedTimeslot && lockedTimeslot[0]?.terminFahrzeugModel) {
          const vehicleData = lockedTimeslot[0].terminFahrzeugModel
          
          // Restore all vehicle details from the locked appointment
          this.$store.commit('setNewVehicle', {
            kfzartId: +this.$route.query.kfzartid,
            brand: vehicleData.marke,
            model: vehicleData.typ,
            licensePlate: vehicleData.kennzeichen,
            fin: vehicleData.fin,
            fourWheelDrive: vehicleData.allrad,
            engine: vehicleData.diesel ? 'Diesel' : 'Benzin',
            gebremst: vehicleData.gebremst,
          })
        } else {
          // Fallback: only set kfzartId if no locked timeslot available
          this.$store.commit('setNewVehicle', { kfzartId: +this.$route.query.kfzartid })
        }
      }
    },
    /**
     * Loads station data and ensures activeStationId is set before continuing.
     * Uses retry logic to wait for async mutation to complete.
     */
    async populateStation() {
      await this.$store.dispatch('getStationMappings', this.$route.params.station)
      
      // Wait for activeStationId to be actually set in store (async operation)
      // This prevents race conditions where subsequent code tries to use the station ID before it's ready
      let retries = 0
      const maxRetries = 50
      while (this.$store.state.station.activeStationId === null && retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 10))
        retries++
      }
      
      if (this.$store.state.station.activeStationId === null) {
        console.error('Failed to load station data after', maxRetries * 10, 'ms')
        throw new Error('Failed to load station data')
      }
    },
    async populateAppointment() {
      await this.$store.dispatch('getSelectedTimeslot', {
        date: this.$route.query.tag,
        startTime: +this.$route.query.von,
      })
      await this.$store.dispatch('lockAppointmentTimeslot')
    },
    /**
     * Validates that all required data for appointment booking is present.
     * Prevents race conditions by ensuring data is loaded before booking attempts.
     * 
     * @returns {boolean} True if all required data is available
     */
    validateRequiredData() {
      return !!(
        this.$store.state.user.user?.pnr &&
        this.$store.state.user.user?.firstname &&
        this.$store.state.user.user?.lastname &&
        this.$store.state.user.user?.email &&
        this.$store.state.station.activeStationId &&
        this.$store.state.service.selectedServicesId?.length &&
        (this.$store.state.vehicle.selectedVehicle || this.$store.state.vehicle.newVehicle)
      )
    },
    calculateStep() {
      if (
        this.selectedState &&
        this.hasAllServices &&
        this.kfzartId &&
        this.activeStationId &&
        this.selectedTimeslot
      ) {
        this.goToStep(5)
      } else if (
        this.selectedState &&
        this.hasAllServices &&
        this.kfzartId &&
        this.activeStationId
      ) {
        this.goToStep(4)
      } else if (this.selectedState && this.hasAllServices && this.kfzartId) {
        this.goToStep(3)
      } else if (this.selectedState && this.hasAllServices) {
        this.goToStep(2)
      } else if (this.selectedState) {
        this.goToStep(1)
      } else {
        this.goToStep(0)
      }
    },
    goToStep(urlStep) {
      this.$store.commit('setActiveStep', urlStep)
      if (urlStep > 1) this.$store.commit('setAllPreviousStepDetailsComplete', urlStep)

      switch (this.activeStep) {
        case 0: {
          this.$router.push({ name: 'Home' })
          break
        }
        case 1: {
          this.$router.push({
            name: 'Service',
            query: { bundesland: this.$route.query.bundesland },
          })
          break
        }
        case 2: {
          this.$router.push({
            name: 'Vehicle',
            params: { services: this.$route.params.services },
            query: { bundesland: this.$route.query.bundesland },
          })
          break
        }
        case 3: {
          this.$router.push({
            name: 'Station',
            params: { services: this.$route.params.services },
            query: {
              bundesland: this.$route.query.bundesland,
              ...(this.selectedVehicle && { kfzid: this.$route.query.kfzid }),
              ...(!this.selectedVehicle && { kfzartid: this.$route.query.kfzartid }),
            },
          })
          break
        }
        case 4: {
          this.$router.push({
            name: 'Appointment',
            params: {
              services: this.$route.params.services,
              station: this.$route.params.station,
            },
            query: {
              bundesland: this.$route.query.bundesland,
              ...(this.selectedVehicle && { kfzid: this.$route.query.kfzid }),
              ...(!this.selectedVehicle && { kfzartid: this.$route.query.kfzartid }),
            },
          })
          break
        }
        case 5: {
          this.$router.push({
            name: 'Overview',
            params: { ...this.$route.params },
            query: { ...this.$route.query },
          })
          break
        }
      }
    },
  },
}
</script>

<style lang="scss">
#oeamtc-page-header--small {
  z-index: $zindex-page-header;
}

.oeamtc-webheader {
  position: sticky;
  top: 0;
  z-index: $zindex-page-header;
}
</style>
